import React, { useMemo } from 'react';
import X from '@uber/base-icons/x';
import { useStyletron } from 'baseui';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { useAtomValue } from 'jotai';
import { type DrawerStyleOverridesT, drawersAtom } from './drawer-stack';
import { useDrawer } from './use-drawer';
type DrawerProps = {
  styleOverrides?: DrawerStyleOverridesT;
};
export function Drawer({
  styleOverrides
}: DrawerProps) {
  const [css, theme] = useStyletron();
  const drawers = useAtomValue(drawersAtom);
  const drawer = useDrawer();
  const drawerProps = useMemo(() => ({
    onClose: drawer.close,
    openDrawer: drawer.open,
    styleOverrides
  }), [drawer.close, drawer.open, styleOverrides]);
  if (drawers.length === 0) {
    return null;
  }
  return <>
      {drawers.map((item, index) => {
      const isTop = index === drawers.length - 1;
      const zIndex = 0 + index;
      return <div key={item.id} data-testid={`drawer-${item.id}`} className={css({
        // Positioned relative to the nearest positioned ancestor
        // (the side panel container), which already occupies exactly
        // the space below whatever header stack the layout renders.
        // This avoids hardcoding a header height here.
        position: 'absolute',
        top: 0,
        right: 0,
        width: '335px',
        height: '100%',
        backgroundColor: theme.colors.backgroundPrimary,
        zIndex,
        transform: isTop ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        animation: isTop ? 'slideIn 300ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
        ...styleOverrides?.body,
        ...item.styleOverrides?.body,
        '@keyframes slideIn': {
          from: {
            transform: 'translateX(100%)'
          },
          to: {
            transform: 'translateX(0)'
          }
        }
      })}>
            <div data-testid="drawer-header" className={css({
          display: 'flex',
          flexDirection: 'column',
          padding: theme.sizing.scale500,
          borderBottom: `2px solid ${theme.colors.borderOpaque}`,
          flexShrink: 0,
          ...styleOverrides?.header,
          ...item.styleOverrides?.header
        })}>
              <div className={css({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          })}>
                <div className={css({
              display: 'flex',
              alignItems: 'center',
              gap: theme.sizing.scale550,
              minWidth: 0
            })}>
                  <div className={css({
                display: 'flex',
                alignItems: 'center',
                gap: theme.sizing.scale300
              })}>
                    {item.titleStartEnhancer}
                    <div data-testid="drawer-title" className={css({
                  ...theme.typography.LabelMedium,
                  display: 'flex',
                  alignItems: 'center'
                })}>
                      {item.title}
                    </div>
                  </div>
                  <div className={css({
                display: 'flex',
                alignItems: 'center'
              })}>
                    {item.titleEndEnhancer}
                  </div>
                </div>
                <Button data-testid="drawer-close-button" kind={KIND.tertiary} size={SIZE.compact} shape={SHAPE.circle} onClick={() => drawer.close({
              open: drawer.open
            })}>
                  <X size={theme.sizing.scale500} />
                </Button>
              </div>
              {item.description && <div data-testid="drawer-description" className={css({
            ...theme.typography.LabelXSmall,
            margin: 0,
            color: theme.colors.contentSecondary
          })}>
                  {item.description}
                </div>}
            </div>

            <div data-testid="drawer-content" className={css({
          flex: 1,
          overflow: 'auto',
          padding: theme.sizing.scale600,
          ...styleOverrides?.content,
          ...item.styleOverrides?.content
        })}>
              {item.content({
            ...drawerProps,
            props: item.props
          })}
            </div>
          </div>;
    })}
    </>;
}